import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken, setPersistence, browserLocalPersistence, onIdTokenChanged } from 'firebase/auth';
import { getFirestore, collection, onSnapshot, DocumentSnapshot } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { environment } from 'src/environments/environment';
import { SessionStorageKeys } from '../enum/session-storage-keys';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import * as Util from '../helpers/utils';
import { IGenericResponse } from '../interfaces/reponse.interface';
import { filter } from 'rxjs/operators';
@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  private auth: any;
  private db: any;
  private storage: any;
  private firebaseAppInitialized = false;

  private permissions: any[] = [];
  private permissionsSubject = new BehaviorSubject<any[] | null>(null);

  constructor(
    private http: HttpClient
  ) {}

  private isReauthenticating = false;

  private async initializeFirebase() {
    if (!this.firebaseAppInitialized) {
      initializeApp(environment.firebase);
      this.auth = getAuth();

      await setPersistence(this.auth, browserLocalPersistence);

      this.db = getFirestore();
      this.storage = getStorage();
      this.firebaseAppInitialized = true;
    }
  }

  authenticateWithToken (): Promise<void> {
    return new Promise((resolve, reject) => {
      this.initializeFirebase();

      const sessionData = JSON.parse(sessionStorage.getItem('SESSION') || '{}');
      const token = sessionData?.auth?.customToken;

      if (token) {
        signInWithCustomToken(this.auth, token).then(() => {
          this.listenTokenChanges();
          resolve();
        });
      } else {
        reject('No token found');
      }
    });
  }

  private listenTokenChanges() {
    onIdTokenChanged(this.auth, async (user) => {
      if (user) {
        const token = await user.getIdToken();
        console.log('Firebase renovó el token automáticamente');
      } else {
        console.warn('Usuario deslogueado de Firebase');
      }
    });
  }

  getCurrentUser () {
    return this.auth.currentUser;
  }

  // esta quedó reutilizable, se está llamando desde app.component para escuchar la colección "users"->uid->permissions
  listenToFirestoreSubcollection (collectionName: string, docId: string, subcollectionName: string): Observable<any[]> {
    return new Observable(observer => {
      const subcollectionRef = collection(this.db, collectionName, docId, subcollectionName);

      const unsubscribe = onSnapshot(subcollectionRef, (snapshot) => {
        const data = snapshot.docs.map((doc: DocumentSnapshot) => ({
          id: doc.id,
          ...doc.data()
        }));

        observer.next(data);
      }, (error) => {
        observer.error(error);
      });

      return () => unsubscribe();
    });
  }

  // llamado al endpoint cada vez que se "escuchan" cambios en la colección permissions dentro del usuario
  getUserPermissions (userId: string): Observable<IGenericResponse> {
    const url: string = `${Util.getUrl('/session/permissions')}/${userId}`;
    return this.http.get<IGenericResponse>(url);
  }

  // había agregado este get y set pero creo que queda mejor con el observable, igual estos se pueden quedar por si se quisieran usar
  get getPermissions (): string[] {
    return this.permissions;
  }

  set setPermissions (newPermissions: string[]) {
    this.permissions = newPermissions;
  }

  // este hay que consumirlo donde se requieran obtener los permisos actualizados
  getPermissionsObservable(): Observable<any[]> {
    return this.permissionsSubject.asObservable().pipe(
    // Espera a que haya permisos válidos
    // Puedes usar filter solo para null o también para vacío
    // filter((perms:any) => Array.isArray(perms) && perms.length > 0)
    filter((perms: any[] | null): perms is any[] => Array.isArray(perms) && perms.length > 0)
  );
    // return this.permissionsSubject.asObservable();
  }

  // este se dispara automáticamente para actualizar el arreglo de permisos, se dispara desde app.component
  updatePermissions (newPermissions: any[]) {
    this.permissionsSubject.next(newPermissions);
  }

  // puede recibir solo el modulo para verificar o modulo y permiso, para validar el rol se podría modificar esta función o solo hacerlo directamente al obtener los permisos de getPermissionsObservable buscar el rol requerido
  checkModuleAccess(module: string, action?: string): boolean {
    let modulePermissions: any = undefined;
    const permissions = this.permissionsSubject.getValue();

    // Si aún no hay permisos, denegar el acceso
    if (!permissions) {
      console.warn('Intento de acceso antes de cargar los permisos');
      return false;
    }

    modulePermissions = permissions.find((permission: any) => permission.module === module);

    if (module && modulePermissions && !action) {
      return true;
    } else {
      if (modulePermissions && modulePermissions.actions[`${action}`] === true) {
        return true;
      }
    }

    return false;
  }

  async reauthenticateFirebase(): Promise<void> {
  // this.initializeFirebase();

  const sessionData = JSON.parse(sessionStorage.getItem('SESSION') || '{}');
  const token = sessionData?.auth?.customToken;

  await this.auth.signOut();

  if (!token) {
    sessionStorage.removeItem(SessionStorageKeys.SESSION);
    window.location.href = '/login';
    throw new Error('No hay token');
  }

  await signInWithCustomToken(this.auth, token);
  console.log('Reautenticado exitosamente');

  this.listenTokenChanges(); // mantener escuch a de token
}
    // reauthenticateFirebase(): Promise<void> {
    //   this.initializeFirebase();

    //   const sessionData = JSON.parse(sessionStorage.getItem('SESSION') || '{}');
    //   const token = sessionData?.auth?.customToken;

    //   return this.auth.signOut().then(() => {
    //     if (token) {
    //       return signInWithCustomToken(this.auth, token)
    //         .then(() => {
    //           console.log('Reautenticado exitosamente');
    //           this.listenTokenChanges();
    //         })
    //         .catch((error) => {
    //           console.error('Error al reautenticar:', error);
    //           sessionStorage.removeItem(SessionStorageKeys.SESSION);
    //           window.location.href = '/auth/login';
    //           throw error;
    //         });
    //     } else {
    //       sessionStorage.removeItem(SessionStorageKeys.SESSION);
    //       // sessionStorage.clear();
    //       window.location.href = '/login';
    //       return Promise.reject('No hay token');
    //     }
    //   });
    // }


  /**
   * Sube un archivo a Firebase Storage SIN convertirlo a base64
   * @param path ruta donde se guardará (ej: 'documents/')
   * @param file archivo tipo File
   * @returns Promise<string> URL de descarga

  uploadFileToPath(file: File, path: string): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        // Asegurar que la ruta termine con /
        if (!path.endsWith('/')) {
          path = path + '/';
        }
        const filePath = `${path}`;
        const storageRef = ref(this.storage, filePath);
        // Subir archivo
        const snapshot = await uploadBytes(storageRef, file);
        // Obtener URL
        const url = await getDownloadURL(snapshot.ref);
        resolve(url); // Todo salió bien
      } catch (error) {
        reject(error); // Ocurrió un error
      }
    });
  }
  */

  uploadFileToPath(file: File, path: string): { uploadTask: Promise<string>, progress$: Observable<number> } {

    if (!path.endsWith('/')) {
      path = path + '/';
    }

    const filePath = `${path}`;
    const storageRef = ref(this.storage, filePath);
    const uploadTaskFB = uploadBytesResumable(storageRef, file);

    // Observable que emite el porcentaje real
    const progress$ = new Observable<number>((observer) => {
      uploadTaskFB.on(
        'state_changed',
        (snapshot) => {
          const progress =
            Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          observer.next(progress);
        },
        (error) => observer.error(error),
        () => observer.complete()
      );
    });

    // Promesa que entrega la URL final (igual que antes)
    const uploadTask = new Promise<string>((resolve, reject) => {
      uploadTaskFB.on(
        'state_changed',
        null,
        (error) => reject(error),
        async () => {
          const url = await getDownloadURL(uploadTaskFB.snapshot.ref);
          resolve(url);
        }
      );
    });

    return { uploadTask, progress$ };
  }

  async getUrlPublic(path: string): Promise<string> {
    try {
      const storage = getStorage();
      const imageRef = ref(storage, path);
      const url = await getDownloadURL(imageRef);
      return url; // resolve
    } catch (error) {
      throw error; // reject
    }
  }

}
